import {Avatar, Input, Spin} from 'antd'
import _ from 'lodash'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {BsPersonCircle} from 'react-icons/bs'
import {HiCheckCircle} from 'react-icons/hi'
import {IoCloseOutline} from 'react-icons/io5'
import {lookup_lookupInfoByValue_Api} from '../../apis'
import useAsyncAction from '../../modules/asyncCache/useAsyncAction'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const LOOKUP_DEBOUNCE = 700

// Ô email liên hệ kèm tìm tài khoản DỰ PHÒNG - tiện ích thêm cho trường hợp admin QUÊN dùng ô tìm
// kiếm 🔍 ở "Tên đầy đủ" phía trên (bạn góp ý nhiều người bỏ qua icon đó - không phải bug, đây là
// tính năng bổ sung). CHỈ kích hoạt khi "Tên đầy đủ" phía trên CHƯA liên kết tài khoản nào
// (form.values.user rỗng) - nếu đã liên kết rồi thì email chỉ còn là thông tin liên hệ thuần, gõ lại
// KHÔNG dò tìm nữa. Tìm thấy tài khoản trùng email KHÔNG tự động gắn liên kết - hiện thẻ gợi ý kèm 2
// lựa chọn tường minh của admin: bấm "Điền thông tin & liên kết" (onApply, đúng logic ghi đè toàn bộ
// field như luồng tìm theo Tên đầy đủ - xem buildTeacherProfilePatch ở TeacherForm.js) hoặc bấm X để
// bỏ qua gợi ý này (giữ nguyên dữ liệu đang nhập, không liên kết). Không tự làm gì khi CHƯA bấm chọn -
// tránh ghi đè âm thầm dữ liệu admin đã nhập tay khác với hồ sơ tài khoản.
// CHỈ dùng cho Teacher - Student KHÔNG áp dụng (email học viên nhiều khả năng là email liên hệ của
// phụ huynh, tự liên kết vào tài khoản phụ huynh là sai - xem comment ở StudentForm.js).
export const AutoLinkEmailInput = ({name, value, onChange, form, placeholder, onApply}) => {
    const t = useTranslate()
    const [isChecking, setIsChecking] = useState(false)
    const [foundInfo, setFoundInfo] = useState(null)
    const [dismissed, setDismissed] = useState(false)
    const debounceRef = useRef(null)

    const {response, success, handleAsyncAction: lookupByEmail} = useAsyncAction({
        apiInfo: lookup_lookupInfoByValue_Api,
    })
    const lookupRef = useRef(lookupByEmail)
    lookupRef.current = lookupByEmail

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
    }, [])

    useEffect(() => {
        setIsChecking(false)
        const info = success ? _.get(response, 'data', {}) : null
        setFoundInfo(info?.owner?.id ? info : null)
    }, [success, response])

    const triggerLookup = useCallback((email) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        setIsChecking(false)
        setFoundInfo(null)
        setDismissed(false)
        if (form?.values?.user) return
        if (!EMAIL_REGEX.test(email ?? '')) return
        setIsChecking(true)
        debounceRef.current = setTimeout(() => {
            lookupRef.current({}, {
                search_type: 'user',
                input_type: 'email',
                input_value: email,
            })
        }, LOOKUP_DEBOUNCE)
    }, [form])

    const handleChange = useCallback((e) => {
        onChange(e)
        triggerLookup(e?.target?.value)
    }, [onChange, triggerLookup])

    const handleApply = useCallback(() => {
        onApply(foundInfo)
        setFoundInfo(null)
    }, [onApply, foundInfo])

    const alreadyLinked = !!form?.values?.user
    const showSuggestion = !alreadyLinked && !dismissed && !!foundInfo

    return (
        <div className="flex flex-col gap-1">
            <Input
                type="email"
                name={name}
                value={value}
                onChange={handleChange}
                allowClear={true}
                autoComplete="false"
                placeholder={placeholder ?? t('ex: email')}
                suffix={isChecking ? <Spin size="small"/> : null}
            />
            {!alreadyLinked && isChecking && (
                <span className="text-2xs text-color-300 italic tracking-wider">{t('searching')}...</span>
            )}
            {showSuggestion && (
                <div className="flex flex-col gap-1.5 p-2 border border-green-300 bg-green-50 rounded-lg">
                    <span className="flex items-center gap-1 text-2xs text-green-600 tracking-wider">
                        <HiCheckCircle size={12} className="flex-shrink-0"/>
                        {t('this email address is associated with the account')}
                    </span>
                    <div className="flex items-center gap-2">
                        <Avatar
                            size={26}
                            src={getAvatar(foundInfo.owner)}
                            icon={<BsPersonCircle size={14} className="text-color-500"/>}
                            className="flex-shrink-0 background-200"
                        />
                        <span className="flex-1 min-w-0 font-bold text-xs text-color-000 truncate">
                            {getTitle(foundInfo.owner)}
                        </span>
                        <button
                            type="button"
                            onClick={handleApply}
                            className="flex-shrink-0 px-2.5 py-1 rounded-md text-2xs font-semibold bg-primary text-white hover:opacity-85 cursor-pointer border-0">
                            {t('fill and link account')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setDismissed(true)}
                            title={t('ignore')}
                            style={{width: 24, height: 24}}
                            className="flex-shrink-0 flex flex-center rounded-md text-green-600 hover:bg-red-100 hover:text-red-500 cursor-pointer transition-colors">
                            <IoCloseOutline size={16}/>
                        </button>
                    </div>
                </div>
            )}
            {!alreadyLinked && !isChecking && !showSuggestion && (
                <span className="text-2xs text-color-300 italic tracking-wider">{t('teacher email auto link hint')}</span>
            )}
        </div>
    )
}

export default AutoLinkEmailInput
